import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Info, Eye, Code } from 'lucide-react';
import { ReplyTemplatesApi, PreviewApi, PageSettingsApi, type ReplyTemplateItem, type Brand } from '@/lib/admin-api';
import { CrudManager, type CrudField } from '@/components/CrudManager';


/**
 * ตัวแปร {{...}} ที่ generator แทนค่าให้ — มี label ไทย + emoji + source สำหรับ chip + tooltip
 * source = หน้าใน Dashboard ที่ใช้แก้ค่านี้ (เพื่อให้ admin คลิกไปแก้ได้)
 */
export const KNOWN_PLACEHOLDERS: Record<
  string,
  { label: string; emoji: string; desc: string; source: string }
> = {
  greeting: {
    label: 'ทักทาย', emoji: '🌸',
    desc: 'คำทักทายลูกค้า — บอตเลือก ใหม่/เก่า ให้อัตโนมัติ',
    source: 'Brand Settings → ทักทายลูกค้าใหม่/เก่า',
  },
  productLine: {
    label: 'ชื่อสินค้า', emoji: '🛍️',
    desc: 'คำโปรยแนะนำสินค้า',
    source: 'Brand Settings → product line',
  },
  piecePriceLine: {
    label: 'ราคาแยกชิ้น', emoji: '💰',
    desc: 'เช่น "เสื้อ 690 ฿ / กางเกง 690 ฿"',
    source: 'ราคา → สินค้าประเภท piece',
  },
  bundlePriceLine: {
    label: 'ราคาชุด', emoji: '👕',
    desc: 'เช่น "1 ชุด = 2 ชิ้น"',
    source: 'ราคา → สินค้าประเภท bundle',
  },
  newPromoLines: {
    label: 'โปรลูกค้าใหม่', emoji: '🎁',
    desc: 'สรุปโปรสำหรับลูกค้าใหม่ทั้งหมด',
    source: 'โปรโมชั่น → audience = ใหม่',
  },
  returningTierLines: {
    label: 'ลดลูกค้าเก่า', emoji: '🏷️',
    desc: 'ราคาลดตามจำนวนชุดของลูกค้าเก่า',
    source: 'โปรโมชั่น → audience = เก่า',
  },
  freeBenefitsText: {
    label: 'ของฟรี', emoji: '✨',
    desc: 'ของแถม เช่น ปักชื่อฟรี/ส่งฟรี',
    source: 'Brand Settings → free benefits',
  },
  colorsText: {
    label: 'สีที่มี', emoji: '🎨',
    desc: 'รายการสีในสต๊อก',
    source: 'Brand Settings → colors text',
  },
  materialDetails: {
    label: 'เนื้อผ้า', emoji: '🧵',
    desc: 'รายละเอียดเนื้อผ้า',
    source: 'Brand Settings → material details',
  },
  leadTimeText: {
    label: 'เวลาส่ง', emoji: '🚚',
    desc: 'พรีออเดอร์กี่วัน',
    source: 'Brand Settings → lead time',
  },
  lineUrl: {
    label: 'ลิงก์ LINE', emoji: '📱',
    desc: 'LINE OA สำหรับสั่งซื้อ',
    source: 'Brand Settings → LINE URL',
  },
  cta: {
    label: 'คำเรียกซื้อ', emoji: '👉',
    desc: 'CTA ปิดท้าย — บอตเลือก ใหม่/เก่า ให้อัตโนมัติ',
    source: 'Brand Settings → CTA ใหม่/เก่า',
  },
  bankAccount: {
    label: 'บัญชีรับโอน', emoji: '🏦',
    desc: 'ข้อมูลบัญชีธนาคาร (ชื่อ + เลขบัญชี + พร้อมเพย์) — ใช้แสดงเมื่อปิดการขาย',
    source: 'Brand Settings → ข้อมูลบัญชีรับโอน',
  },
};

const KNOWN_TEMPLATE_KEYS = [
  { key: 'scrub_promo_new', desc: 'ตอบลูกค้าใหม่ Cher Scrub' },
  { key: 'scrub_promo_returning', desc: 'ตอบลูกค้าเก่า Cher Scrub' },
  { key: 'bow_promo_new', desc: 'ตอบลูกค้าใหม่ Cherlyn' },
  { key: 'bow_promo_returning', desc: 'ตอบลูกค้าเก่า Cherlyn' },
];

function HelpBanner() {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-blue-200 bg-blue-50/50 rounded-lg p-3 text-sm">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 font-medium text-blue-800 hover:underline w-full text-left"
      >
        <Info size={16} />
        Template ใช้ยังไง? — {open ? 'ซ่อน' : 'ดู'}
      </button>
      {open && (
        <div className="mt-3 text-xs space-y-2">
          <p className="text-muted-foreground">
            บอตจะเลือก template ตามแบรนด์ + ประเภทลูกค้า โดย matching <code>template_key</code>:
          </p>
          <ul className="ml-4 space-y-0.5 list-disc text-muted-foreground">
            {KNOWN_TEMPLATE_KEYS.map((t) => (
              <li key={t.key}>
                <code>{t.key}</code> — {t.desc}
              </li>
            ))}
          </ul>
          <p className="text-muted-foreground mt-2">
            ใน <strong>body</strong> ใช้ <code>{`{{variable}}`}</code> เพื่อให้ generator แทนค่า. รายการ placeholders ที่ใช้ได้:
          </p>
          <div className="border border-blue-200 rounded p-2 bg-white">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="py-1 w-48">กล่องสี (chip)</th>
                  <th className="py-1">ใช้ทำอะไร</th>
                  <th className="py-1">แก้ได้ที่หน้า</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(KNOWN_PLACEHOLDERS).map(([key, p]) => (
                  <tr key={key} className="border-t border-blue-100">
                    <td className="py-1">
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 border border-blue-300 text-xs">
                        {p.emoji} {p.label}
                      </span>
                    </td>
                    <td className="py-1 text-muted-foreground">{p.desc}</td>
                    <td className="py-1 text-muted-foreground italic">{p.source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-muted-foreground">
            แก้แล้ว cache จะถูกล้างอัตโนมัติ — ทดสอบได้ทันทีที่แท็บ "ทดสอบบอต"
          </p>
        </div>
      )}
    </div>
  );
}

export default function ReplyTemplatesPage() {
  const VAR_KEYS = Object.keys(KNOWN_PLACEHOLDERS);
  const FIELDS: CrudField[] = [
    { key: 'templateKey', label: 'รหัสเทมเพลต (key)', type: 'text', required: true, placeholder: 'scrub_promo_new', disabledOnEdit: true, help: 'บอตเลือกเทมเพลตตาม key นี้ (ผูกโค้ด) — ห้ามเปลี่ยนภายหลัง' },
    { key: 'name', label: 'ชื่อเรียก (ภายใน)', type: 'text', required: true, help: 'ชื่อให้แอดมินจำง่าย' },
    { key: 'body', label: 'ข้อความที่บอตจะตอบ', type: 'textarea', required: true, insertVars: VAR_KEYS, help: 'พิมพ์ได้เลย · กดปุ่ม “แทรกตัวแปร” เพื่อให้บอตเติมข้อมูลจริง (ราคา/สี/ไซซ์)' },
    { key: 'imageUrl', label: 'รูปแนบ (ถ้ามี)', type: 'image', help: 'ส่งรูปพร้อมข้อความ' },
    { key: 'notes', label: 'หมายเหตุ', type: 'text', help: 'บันทึกภายใน' },
    { key: 'active', label: 'เปิดใช้งาน', type: 'toggle', default: true },
  ];
  return (
    <CrudManager<ReplyTemplateItem>
      title="💬 เทมเพลตคำตอบ (Reply Templates)"
      addLabel="เพิ่มเทมเพลต"
      help="ข้อความที่บอตพูด — ใช้ {{ตัวแปร}} ให้บอตเติมค่าจริง (ราคา/สี/ไซซ์). กดขยายแต่ละแถวเพื่อดู “ตัวอย่างที่ลูกค้าเห็นจริง”. แก้แล้ว cache ล้างอัตโนมัติ"
      queryKey="reply-templates"
      api={ReplyTemplatesApi}
      fields={FIELDS}
      brandScoped
      searchKeys={['name', 'templateKey', 'body']}
      sortRows={(a, b) => a.templateKey.localeCompare(b.templateKey)}
      rowTitle={(t) => <>{t.name} <code className="text-[11px] text-muted-foreground">{t.templateKey}</code></>}
      rowSubtitle={(t) => <PrettyTemplateBody body={t.body} />}
      renderRowExtra={(t) => <LivePreview brand={t.brand} templateKey={t.templateKey} />}
      headerExtra={<HelpBanner />}
    />
  );
}

// ============================================================
// PrettyTemplateBody — แสดง template body โดยแปลง {{X}} เป็น chip สีน้ำเงิน
// + ปุ่ม toggle ดูโค้ดดิบ (สำหรับ developer)
// ============================================================
function PrettyTemplateBody({ body }: { body: string }) {
  const [showCode, setShowCode] = useState(false);
  const parts = body.split(/(\{\{\w+\}\})/g);
  return (
    <div className="mt-2 space-y-1">
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground flex items-center gap-1">
          <Eye size={12} /> เทมเพลตข้อความ
          <span className="text-[10px]">(กล่องสีฟ้า = จุดที่บอตจะเติมข้อมูลจริงตอนตอบลูกค้า)</span>
        </div>
        <button
          onClick={() => setShowCode(!showCode)}
          className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-1"
          title="ดูแบบ raw text"
        >
          <Code size={10} /> {showCode ? 'ซ่อนโค้ด' : 'ดูโค้ด'}
        </button>
      </div>
      <div className="p-3 bg-muted/30 rounded border border-border text-sm whitespace-pre-wrap leading-relaxed">
        {parts.map((p, i) => {
          const m = p.match(/^\{\{(\w+)\}\}$/);
          if (m) {
            const key = m[1]!;
            const meta = KNOWN_PLACEHOLDERS[key];
            return (
              <span
                key={i}
                className="inline-flex items-center px-1.5 py-0.5 mx-0.5 rounded bg-blue-100 text-blue-800 border border-blue-300 text-xs cursor-help"
                title={meta ? `${meta.desc}\n📍 แก้ได้ที่: ${meta.source}` : `Placeholder: ${key}`}
              >
                {meta ? `${meta.emoji} ${meta.label}` : `❓ ${key}`}
              </span>
            );
          }
          return <span key={i}>{p}</span>;
        })}
      </div>
      {showCode && (
        <pre className="text-[10px] p-2 bg-muted/20 rounded border border-border whitespace-pre-wrap font-mono text-muted-foreground max-h-32 overflow-auto">
          {body}
        </pre>
      )}
    </div>
  );
}

// ============================================================
// LivePreview — เรียก /api/admin/preview ด้วย text trigger ที่ทำให้บอตเลือก template นี้
// แสดง "ลูกค้าจะเห็นแบบนี้" — ทั้งใหม่ + เก่า
// ============================================================
function LivePreview({ brand, templateKey }: { brand: Brand; templateKey: string }) {
  const [open, setOpen] = useState(false);
  const [selectedPageId, setSelectedPageId] = useState('');
  const pages = useQuery({
    queryKey: ['page-settings'],
    queryFn: PageSettingsApi.list,
    enabled: open,
  });
  const matchingPages = (pages.data ?? []).filter((page) => page.brand === brand);
  const pageId = matchingPages.length === 1
    ? matchingPages[0]!.pageId
    : matchingPages.find((page) => page.pageId === selectedPageId)?.pageId;
  // Trigger keyword ที่ทำให้บอตเข้า promo flow → match template
  // (text=ราคา → keyword 'ราคา' = promo intent → generatePromoReply เลือก template ตาม customerType)
  const triggerText = 'ราคาเท่าไหร่คะ';
  const isNewTemplate = templateKey.endsWith('_new');
  const isReturningTemplate = templateKey.endsWith('_returning');

  const q = useQuery({
    queryKey: ['preview', brand, templateKey, pageId],
    queryFn: async () => {
      if (!pageId) throw new Error('กรุณาเลือกเพจของแบรนด์นี้ก่อนทดสอบ');
      const results: Record<string, string> = {};
      if (isNewTemplate || (!isNewTemplate && !isReturningTemplate)) {
        // new customer = no customerId
        const r = await PreviewApi.run({ text: triggerText, pageId, previewCustomerType: 'new' });
        results.new = r.reply ?? '(ไม่มี reply)';
      }
      if (isReturningTemplate) {
        // Simulate customer type without reading or changing an actual customer.
        const r = await PreviewApi.run({ text: triggerText, pageId, previewCustomerType: 'returning' });
        results.returning = r.reply ?? '(ไม่มี reply)';
      }
      return results;
    },
    enabled: open && !!pageId && pages.isSuccess,
    staleTime: 30_000,
  });

  return (
    <div className="mt-2">
      <button
        onClick={() => setOpen(!open)}
        className="text-xs flex items-center gap-1 text-green-700 hover:underline"
      >
        <Eye size={12} />
        {open ? 'ซ่อนตัวอย่างจริง' : '👁 ดูตัวอย่างที่ลูกค้าจะเห็นจริง'}
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          {pages.isLoading && <div className="text-xs text-muted-foreground">กำลังโหลดเพจ…</div>}
          {pages.isError && <div className="text-xs text-red-600">โหลดเพจไม่สำเร็จ: {String(pages.error)}</div>}
          {pages.isSuccess && (
            <label className="block text-xs space-y-1">
              <span>เพจที่ใช้ทดสอบ (เฉพาะแบรนด์ของเทมเพลตนี้)</span>
              <select
                aria-label="เพจที่ใช้ทดสอบเทมเพลต"
                className="block w-full rounded border border-input bg-background p-1.5"
                value={pageId ?? ''}
                disabled={matchingPages.length === 0 || q.isFetching}
                onChange={(event) => setSelectedPageId(event.target.value)}
              >
                {matchingPages.length !== 1 && <option value="">{matchingPages.length === 0 ? 'ยังไม่มีเพจสำหรับแบรนด์นี้' : 'เลือกเพจก่อนทดสอบ'}</option>}
                {matchingPages.map((page) => <option key={page.pageId} value={page.pageId}>{page.pageName} ({page.pageId})</option>)}
              </select>
              {matchingPages.length === 0 && <span>ตั้งค่าเพจและแบรนด์ในเมนู Pages ก่อนทดสอบ</span>}
            </label>
          )}
          {q.isLoading && <div className="text-xs text-muted-foreground">กำลังโหลด…</div>}
          {q.isError && (
            <div className="text-xs text-red-600">โหลดไม่สำเร็จ: {String(q.error)}</div>
          )}
          {q.data?.new && (
            <PreviewBox
              label="ลูกค้าใหม่ (ยังไม่เคยซื้อ)"
              color="emerald"
              text={q.data.new}
            />
          )}
          {q.data?.returning && (
            <PreviewBox
              label="ลูกค้าเก่า (เคยซื้อแล้ว)"
              color="amber"
              text={q.data.returning}
            />
          )}
        </div>
      )}
    </div>
  );
}

function PreviewBox({
  label,
  color,
  text,
}: {
  label: string;
  color: 'emerald' | 'amber';
  text: string;
}) {
  const bg = color === 'emerald' ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200';
  const tag = color === 'emerald' ? 'bg-emerald-600' : 'bg-amber-600';
  return (
    <div className={`border rounded p-2 ${bg}`}>
      <div className="flex items-center gap-2 mb-1">
        <span className={`text-[10px] text-white px-1.5 py-0.5 rounded ${tag}`}>{label}</span>
      </div>
      <pre className="text-xs whitespace-pre-wrap font-sans leading-relaxed">{text}</pre>
    </div>
  );
}

